import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Headphones, Pencil, Server, KeyRound, Smartphone } from "lucide-react";
import { fetchStations, updateStation, fetchMembers, fetchMyStation, ROLE_LABELS } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AccountBlocks } from "@/components/AccountBlocks";
import { toast } from "sonner";

const UNASSIGNED = "__libre__";

export const MonPoste = () => { const { data } = useQuery({ queryKey: ["my-station"], queryFn: () => fetchMyStation() });
  if (!data || !data.assigned) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5" data-testid="mon-poste">
        <p className="font-heading font-semibold text-slate-800 mb-1">Mon poste</p>
        <p className="text-sm text-slate-400">Aucun poste ne vous est assigné pour le moment.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 shadow-sm p-5" data-testid="mon-poste">
      <div className="flex items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><Headphones className="h-4 w-4" /></span>
        <p className="font-heading font-semibold text-slate-900">Mon poste — {data.display_name}</p>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div><dt className="text-slate-400 text-xs uppercase tracking-wide">Identifiant Zoiper</dt><dd className="font-medium text-slate-900 tabular-nums" data-testid="zoiper-id">{data.zoiper_id}</dd></div>
        <div><dt className="text-slate-400 text-xs uppercase tracking-wide">Serveur</dt><dd className="font-medium text-slate-900 inline-flex items-center gap-1"><Server className="h-3.5 w-3.5" />{data.server}</dd></div>
        <div className="sm:col-span-2"><dt className="text-slate-400 text-xs uppercase tracking-wide">Mot de passe</dt><dd className="font-medium text-slate-700 inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />Communiqué par l'administrateur</dd></div>
      </dl>
      <div className="mt-4 rounded-lg bg-white border border-slate-200 p-4" data-testid="zoiper-guide">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2"><Smartphone className="h-4 w-4 text-emerald-600" /> Configurer Zoiper</p>
        <div className="space-y-4">
          <div data-testid="zoiper-guide-ios">
            <p className="text-xs tracking-[0.05em] uppercase font-semibold text-slate-500 mb-1.5">iPhone</p>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
              <li>Installez l'application <span className="font-medium">Zoiper</span> depuis l'App Store.</li>
              <li>Ajoutez un compte SIP avec l'identifiant et le serveur ci-dessus (mot de passe fourni par l'administrateur).</li>
              <li>Activez les <span className="font-medium">notifications push</span> pour recevoir les appels même en arrière-plan.</li>
            </ol>
          </div>
          <div data-testid="zoiper-guide-android">
            <p className="text-xs tracking-[0.05em] uppercase font-semibold text-slate-500 mb-1.5">Android</p>
            <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
              <li>Installez l'application <span className="font-medium">Zoiper</span> depuis Google Play.</li>
              <li>Ajoutez un compte SIP avec les mêmes réglages : identifiant <span className="font-medium tabular-nums">{data.zoiper_id}</span>, serveur <span className="font-medium">{data.server}</span> (mot de passe fourni par l'administrateur).</li>
              <li>Dans Zoiper, activez les <span className="font-medium">notifications push</span> (réglages du compte) pour recevoir les appels en arrière-plan.</li>
              <li>Désactivez l'optimisation de batterie pour Zoiper : <span className="font-medium">Paramètres → Applications → Zoiper → Batterie → « Non limitée »</span>, sinon les appels peuvent ne pas sonner quand le téléphone est en veille.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export const StationsTab = () => {
  const qc = useQueryClient();
  const { data: stations = [] } = useQuery({ queryKey: ["stations"], queryFn: fetchStations });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });
  const [rename, setRename] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["stations"] });
    qc.invalidateQueries({ queryKey: ["my-station"] });
    qc.invalidateQueries({ queryKey: ["calls"] });
  };
  const assignMut = useMutation({
    mutationFn: ({ ext, member }) => updateStation(ext, { assigned_member_id: member === UNASSIGNED ? "" : member }),
    onSuccess: (res) => {
      invalidate();
      if (res?.reassigned) {
        toast.success("Poste réassigné", { description: "Pensez à changer le mot de passe du poste dans le standard avant de le confier à quelqu'un d'autre." });
      } else {
        toast.success("Poste mis à jour");
      }
    },
    onError: () => toast.error("Échec de la mise à jour"),
  });

  const renameMut = useMutation({
    mutationFn: () => updateStation(rename, { display_name: renameVal }),
    onSuccess: () => { invalidate(); toast.success("Poste renommé"); setRename(null); },
    onError: () => toast.error("Échec du renommage"),
  });

  return (
    <div className="space-y-4" data-testid="stations-tab">
      <MonPoste />
      <AccountBlocks />

      <div className="space-y-2" data-testid="stations-list">
        {stations.map((s) => (
          <div key={s.extension} data-testid={`station-${s.extension}`} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-3">
            <span className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Headphones className="h-4 w-4 text-slate-500" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-slate-900 text-sm truncate">{s.display_name}</p>
                <button data-testid={`station-rename-${s.extension}`} onClick={() => { setRename(s.extension); setRenameVal(s.display_name); }} className="text-slate-400 hover:text-emerald-700"><Pencil className="h-3.5 w-3.5" /></button>
              </div>
              <p className="text-xs text-slate-400">{s.assigned_member_name || "Libre"}</p>
            </div>
            <Select value={s.assigned_member_id || UNASSIGNED} onValueChange={(v) => assignMut.mutate({ ext: s.extension, member: v })}>
              <SelectTrigger data-testid={`station-assign-${s.extension}`} className="h-9 w-[160px] text-sm shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Libre</SelectItem>
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} — {ROLE_LABELS[m.role] || m.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <Dialog open={!!rename} onOpenChange={(o) => !o && setRename(null)}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Renommer le poste</DialogTitle>
            <DialogDescription className="sr-only">Modifier le nom affiché du poste</DialogDescription>
          </DialogHeader>
          <div><Label className="text-sm">Nom affiché</Label><Input data-testid="station-rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="mt-1" /></div>
          <DialogFooter>
            <button data-testid="station-rename-save" onClick={() => renameMut.mutate()} disabled={!renameVal.trim()} className="rounded-lg px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
